import React, { useEffect, useState } from "react";
import { API, authedFetch } from "../lib/api";

// In-memory cache: key → Promise<string> (blob URL) or resolved string.
const BLOB_CACHE = new Map();
// Persistent browser cache (survives reloads). Paths are immutable (uuid).
const PERSIST_CACHE = "uc-img-v1";

async function fetchBlob(url) {
  const hasCaches = typeof window !== "undefined" && "caches" in window;
  if (hasCaches) {
    try {
      const cache = await caches.open(PERSIST_CACHE);
      const hit = await cache.match(url);
      if (hit) return hit.blob();
      const res = await authedFetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      cache.put(url, res.clone()).catch(() => {});
      return res.blob();
    } catch (e) {
      if (String(e?.message || "").startsWith("HTTP")) throw e;
    }
  }
  const res = await authedFetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.blob();
}

async function getBlobUrl(path, thumb) {
  const key = thumb ? `${path}@${thumb}` : path;
  const cached = BLOB_CACHE.get(key);
  if (cached) return cached;
  const promise = (async () => {
    let b;
    if (thumb) {
      try {
        b = await fetchBlob(`${API}/files/thumb/${path}?w=${thumb}`);
      } catch {
        b = await fetchBlob(`${API}/files/${path}`);
      }
    } else {
      b = await fetchBlob(`${API}/files/${path}`);
    }
    const u = URL.createObjectURL(b);
    BLOB_CACHE.set(key, u);
    return u;
  })();
  BLOB_CACHE.set(key, promise);
  try {
    return await promise;
  } catch (e) {
    BLOB_CACHE.delete(key);
    throw e;
  }
}

/** Renders a protected backend file as <img>. Pass `thumb={px}` to load a
 * small server-side thumbnail (ideal for logos / list views). */
export default function AuthedImage({ path, thumb = null, alt = "", className = "", fallback = null, onError, ...rest }) {
  const key = thumb ? `${path}@${thumb}` : path;
  const [url, setUrl] = useState(() => {
    const c = BLOB_CACHE.get(key);
    return typeof c === "string" ? c : null;
  });
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    setFailed(false);
    if (!path) { setUrl(null); return; }
    const c = BLOB_CACHE.get(key);
    if (typeof c === "string") { setUrl(c); return; }
    (async () => {
      try {
        const u = await getBlobUrl(path, thumb);
        if (alive) setUrl(u);
      } catch {
        if (alive) { setFailed(true); setUrl(null); onError && onError(); }
      }
    })();
    return () => { alive = false; };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps

  if (failed || !url) return fallback;
  return <img src={url} alt={alt} className={className} decoding="async" {...rest} />;
}
